import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPinOff } from "lucide-react";
import { catMeta } from "../lib/advisories";

const DEFAULT_CENTER = [36.27, -96.24];

function pinIcon(color, selected, pending) {
  const size = selected ? 34 : 26;
  const ring = selected ? `box-shadow:0 0 0 4px rgba(37,99,235,.35);` : "";
  const dash = pending ? "border-style:dashed;" : "";
  return L.divIcon({
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    html: `<div style="width:${size}px;height:${size}px;background:${color};border:3px solid #fff;${dash}border-radius:50% 50% 50% 0;transform:rotate(-45deg);${ring}box-shadow:${selected ? "0 0 0 4px rgba(37,99,235,.35)," : ""}0 2px 6px rgba(2,6,23,.45);"></div>`,
  });
}

function FocusOn({ focus, points }) {
  const map = useMap();
  useEffect(() => {
    if (focus) {
      map.flyTo([focus.lat, focus.lng], Math.max(map.getZoom(), 14), { duration: 0.6 });
    }
  }, [focus, map]);
  useEffect(() => {
    if (!focus && points.length) {
      const t = setTimeout(() => {
        map.invalidateSize();
        const b = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
        map.fitBounds(b.pad(0.25), { maxZoom: 13 });
      }, 150);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points.length]);
  return null;
}

function ClickPicker({ onPick }) {
  useMapEvents({ click: (e) => onPick && onPick({ lat: +e.latlng.lat.toFixed(6), lng: +e.latlng.lng.toFixed(6) }) });
  return null;
}

export function MapUnavailable({ message, className = "" }) {
  return (
    <div data-testid="map-unavailable" className={`flex h-full min-h-[220px] flex-col items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center ${className}`}>
      <MapPinOff className="h-6 w-6 text-slate-400" />
      <p className="text-sm font-semibold text-slate-700">Map unavailable</p>
      <p className="max-w-xs text-xs text-slate-500">{message || "The map provider is not configured. Advisories remain fully usable from the list."}</p>
    </div>
  );
}

export default function AdvisoryMap({ config, advisories = [], selectedId, onSelect, focus, pickable, pin, onPick, className = "", testid = "advisory-map" }) {
  if (!config?.map_enabled || !config.tile_url) return <MapUnavailable className={className} />;
  const points = advisories.filter((a) => a.has_location);
  const center = pin ? [pin.lat, pin.lng] : points.length ? [points[0].lat, points[0].lng] : DEFAULT_CENTER;
  return (
    <div data-testid={testid} className={`relative overflow-hidden rounded-md border border-slate-200 ${pickable ? "cursor-crosshair" : ""} ${className}`}>
      <MapContainer center={center} zoom={pin ? 14 : 11} scrollWheelZoom className="h-full w-full" style={{ minHeight: 220 }}>
        <TileLayer url={config.tile_url} attribution={config.tile_attribution || ""} />
        {!pickable && <FocusOn focus={focus} points={points} />}
        {pickable && <ClickPicker onPick={onPick} />}
        {pickable && pin && <Marker position={[pin.lat, pin.lng]} icon={pinIcon("#2563eb", true, false)} draggable eventHandlers={{ dragend: (e) => { const p = e.target.getLatLng(); onPick && onPick({ lat: +p.lat.toFixed(6), lng: +p.lng.toFixed(6) }); } }} />}
        {!pickable && points.map((a) => (
          <Marker
            key={a.id}
            position={[a.lat, a.lng]}
            icon={pinIcon(catMeta(a.category).color, a.id === selectedId, a.effective_status === "needs_verification")}
            eventHandlers={{ click: () => onSelect && onSelect(a) }}
            title={a.title}
          />
        ))}
      </MapContainer>
      {pickable && (
        <div className="pointer-events-none absolute left-2 top-2 z-[1000] rounded bg-slate-900/85 px-2 py-1 text-[11px] font-semibold text-white">
          {pin ? "Drag the pin or tap to move it" : "Tap the map to place a pin"}
        </div>
      )}
    </div>
  );
}
